import { useEffect, useMemo, useState, useRef } from 'react';
import { artistOnLeave } from '@/domain/artistFlow';
import { useNavigate } from 'react-router-dom';
import DepositEmailField, { depositEmailFor } from '@/components/deposits/DepositEmailField';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { generateDepositLink, payBookingDepositAtDesk } from '@/domain/depositFlow';
import { intentFailureCopy } from '@/api/intents';
import {
  priceForArtist,
  depositForSession,
  depositForDuration,
  sessionLengthForDuration,
  SESSION_DURATION_MIN,
} from '@/domain/pricing';
import { aud, customerName, simTimeFull, simDateShort, formatBodyPart } from '@/utils/format';
import { bookingStatusTone, depositShade } from '@/utils/statusTone';
import { inspirationFor, partitionReferences, type ReferenceCandidate } from '@/utils/imageHelpers';
import ReferencePicker from '@/components/ReferencePicker';
import ArtistPicker from '@/components/booking/ArtistPicker';
import PlacementFields, { type PlacementValue } from '@/components/booking/PlacementFields';
import RateCardBlock from '@/components/booking/RateCardBlock';
import type { Booking, DayRateBasis, InvoiceStatus, Piece, SessionLength } from '@/domain/types';
import { X, Save, Send, CalendarClock, Banknote, ChevronDown, AlertTriangle, Unlink } from 'lucide-react';
import clsx from 'clsx';
import { currentActorId } from '@/domain/actor';

const INVOICE_OPTIONS: Array<{ key: InvoiceStatus; label: string }> = [
  { key: 'not-issued', label: 'Not invoiced' },
  { key: 'draft', label: 'Draft' },
  { key: 'issued', label: 'Issued' },
  { key: 'paid', label: 'Paid' },
  { key: 'uninvoiced-writeoff', label: 'Write-off' },
];

interface Props {
  open: boolean;
  bookingId: string;
  onClose: () => void;
}

const STYLE_TAG_OPTIONS = [
  'colour-realism',
  'traditional',
  'neo-traditional',
  'fine-line',
  'lettering',
  'blackwork',
  'sleeve',
  'flash',
  'cover-up',
];

// The booking's own 1-based position in its piece (falls back to the piece's
// next-session number when the booking isn't yet linked).
const sessionIndexInPiece = (piece: Piece, bookingId: string): number => {
  const idx = piece.bookingsLinked.indexOf(bookingId);
  return idx >= 0 ? idx + 1 : (piece.sessionsCompleted ?? piece.bookingsLinked.length) + 1;
};

/**
 * Edit an existing booking in place. Corrects artist, session length and the
 * structured brief; shows status + piece read-only (piece can only be detached,
 * not reassigned); and collects a deposit (Stripe link or in-person method).
 */
export default function EditBookingModal({ open, bookingId, onClose }: Props) {
  const bookings = useBrainStore((s) => s.bookings);
  const customers = useBrainStore((s) => s.customers);
  const artistsMap = useBrainStore((s) => s.artists);
  const pieces = useBrainStore((s) => s.pieces);
  const deposits = useBrainStore((s) => s.deposits);
  const upsertBooking = useBrainStore((s) => s.upsertBooking);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const navigate = useNavigate();

  const booking = bookings[bookingId];
  const customer = booking ? customers[booking.customerId] : undefined;

  const studioArtists = useMemo(
    () =>
      Object.values(artistsMap)
        // tv-hnjy.6: apprentices are bookable (leave rules below apply to them too).
        .filter((a) => a.studioId === booking?.studioId && !a.archived)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    [artistsMap, booking?.studioId],
  );

  const [artistId, setArtistId] = useState('');
  // D10-02: artists on leave on the booking's day aren't offered for a change
  // of artist (the booking's own artist stays, even on a leave day).
  const onLeaveIds = useMemo(
    () =>
      booking
        ? studioArtists.filter((a) => a.id !== booking.artistId && a.id !== artistId && artistOnLeave(a, booking.start)).map((a) => a.id)
        : [],
    [studioArtists, booking, artistId],
  );
  const [sessionLength, setSessionLength] = useState<SessionLength>('half-day');
  const [colour, setColour] = useState<'colour' | 'black-and-grey'>('black-and-grey');
  const [styleTags, setStyleTags] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [selectedRefs, setSelectedRefs] = useState<string[]>([]);
  const [placement, setPlacement] = useState<PlacementValue>({ placementNote: '' });
  const [invoiceStatus, setInvoiceStatus] = useState<InvoiceStatus>('not-issued');

  const [dayRateInput, setDayRateInput] = useState('');
  const [customHours, setCustomHours] = useState<number | null>(null);
  const [dayRateBasis, setDayRateBasis] = useState<DayRateBasis>('standard');
  const [depositInput, setDepositInput] = useState('');
  const [overrideActive, setOverrideActive] = useState(false);
  const [showRateReset, setShowRateReset] = useState(false);

  const [depositMenuOpen, setDepositMenuOpen] = useState(false);
  // D05-11: taking the deposit now needs the client's email (on file, else typed).
  const [manualEmail, setManualEmail] = useState('');
  const depositMenuRef = useRef<HTMLDivElement>(null);
  const [confirmingDetach, setConfirmingDetach] = useState(false);

  // The pool the artist curates from: everything the client sent, plus any
  // already-saved references (so a saved ref never drops out of the grid).
  const referencePool = useMemo(() => {
    const seen = new Set<string>();
    const pool: ReferenceCandidate[] = [];
    for (const p of inspirationFor(customer)) {
      if (seen.has(p.url)) continue;
      seen.add(p.url);
      pool.push({ url: p.url, caption: p.caption, capturedAt: p.capturedAt });
    }
    for (const url of booking?.referencePhotos ?? []) {
      if (seen.has(url)) continue;
      seen.add(url);
      pool.push({ url });
    }
    return pool;
  }, [customer, booking?.referencePhotos]);

  const customerBookings = useMemo(
    () => (customer?.bookings ?? []).map((id) => bookings[id]).filter(Boolean) as Booking[],
    [customer, bookings],
  );

  const references = useMemo(
    () =>
      partitionReferences({
        photos: referencePool,
        bookings: customerBookings,
        // This booking's own refs aren't "used elsewhere"; its selected photos always show.
        currentBookingId: booking?.id,
        currentPieceId: booking?.pieceId,
        selectedUrls: selectedRefs,
        labelForBooking: (b) => simDateShort(b.start),
      }),
    [referencePool, customerBookings, booking?.id, booking?.pieceId, selectedRefs],
  );

  // Re-seed the form whenever a different booking is opened.
  useEffect(() => {
    if (!booking) return;
    const linked = booking.depositId ? deposits[booking.depositId] : undefined;
    const bookingArtist = artistsMap[booking.artistId];
    const piece = booking.pieceId ? pieces[booking.pieceId] : undefined;
    const planRate = piece?.pricingPlan?.find(
      (x) => x.sessionIndex === sessionIndexInPiece(piece, booking.id),
    )?.expectedDayRate;
    const std = planRate ?? (bookingArtist ? priceForArtist(bookingArtist, booking.sessionLength) : 0);

    setArtistId(booking.artistId);
    setSessionLength(booking.sessionLength);
    setColour(booking.colour ?? 'black-and-grey');
    setStyleTags((booking.styleTags ?? []).filter((t) => t !== 'black-and-grey'));
    setNotes(booking.notes ?? '');
    setSelectedRefs(booking.referencePhotos ?? []);
    setInvoiceStatus(booking.invoiceStatus ?? 'not-issued');
    setPlacement({
      bodyPart: booking.bodyPart,
      bodyPartSide: booking.bodyPartSide,
      bodyPartQualifier: booking.bodyPartQualifier,
      placementNote: booking.placement ?? '',
    });
    const tierMin = SESSION_DURATION_MIN[booking.sessionLength];
    const builtFromHours = booking.durationMin !== tierMin;
    setCustomHours(builtFromHours ? booking.durationMin / 60 : null);
    setDayRateBasis(booking.dayRateBasis ?? 'standard');
    setDayRateInput(String(booking.dayRateAud ?? std));
    setDepositInput(
      String(linked?.amountAud ?? depositForSession(booking.sessionLength, artistsMap[booking.artistId])),
    );
    setConfirmingDetach(false);
    setDepositMenuOpen(false);
    setShowRateReset(false);
    setOverrideActive(
      Boolean(
        builtFromHours ||
          (booking.dayRateAud != null && booking.dayRateAud !== std) ||
          (booking.dayRateBasis && booking.dayRateBasis !== 'standard'),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  useEffect(() => {
    if (!depositMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (depositMenuRef.current && !depositMenuRef.current.contains(e.target as Node)) {
        setDepositMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [depositMenuOpen]);

  if (!open || !booking || !customer) return null;

  const linkedDeposit = booking.depositId ? deposits[booking.depositId] : undefined;
  const artist = artistsMap[artistId];
  // D10-02: changing the artist onto one who is on leave that day is refused
  // (the server says `artist-on-leave` too); saving in place is fine.
  const movedOntoLeave = artistId !== booking.artistId && artistOnLeave(artist, booking.start);
  const statusTone = bookingStatusTone(booking.status);
  const shade = depositShade(linkedDeposit);

  const pieceForBooking = booking.pieceId ? pieces[booking.pieceId] : undefined;
  const planRate = pieceForBooking?.pricingPlan?.find(
    (x) => x.sessionIndex === sessionIndexInPiece(pieceForBooking, booking.id),
  )?.expectedDayRate;
  const usingPiecePlan = planRate != null;

  const standardDayRateFor = (session: SessionLength, aid: string) => {
    if (planRate != null) return planRate;
    const a = artistsMap[aid];
    return a ? priceForArtist(a, session) : 0;
  };
  const standardDayRate = standardDayRateFor(sessionLength, artistId);
  const standardDeposit = depositForSession(sessionLength, artist);

  const depositAud =
    depositInput === '' ? standardDeposit : Math.max(0, Math.round(Number(depositInput) || 0));
  const canTakeDeposit = depositAud > 0;
  const manualContactEmail = depositEmailFor(customer, manualEmail);

  const effectiveDurationMin =
    customHours != null ? Math.round(customHours * 60) : SESSION_DURATION_MIN[sessionLength];
  const effectiveSessionLength =
    customHours != null ? sessionLengthForDuration(effectiveDurationMin) : sessionLength;

  const colourLabel = colour === 'colour' ? 'Colour' : 'Black & grey';
  const placementLabel = formatBodyPart({
    bodyPart: placement.bodyPart,
    bodyPartSide: placement.bodyPartSide,
    bodyPartQualifier: placement.bodyPartQualifier,
    placement: placement.placementNote,
  });
  const summary = [placementLabel, colourLabel, styleTags.join(', ')].filter(Boolean).join(' · ');
  const deriveBrief = () => summary || notes || booking.designBrief;

  const syncStandard = (session: SessionLength, aid: string) => {
    setDayRateInput(String(standardDayRateFor(session, aid)));
    setDepositInput(String(depositForSession(session, artistsMap[aid])));
    setDayRateBasis('standard');
    setCustomHours(null);
    setOverrideActive(false);
    setShowRateReset(false);
  };
  const changeSession = (s: SessionLength) => {
    setSessionLength(s);
    syncStandard(s, artistId);
  };
  const changePrimaryArtist = (id: string) => {
    setArtistId(id);
    if (overrideActive) setShowRateReset(true);
    else syncStandard(sessionLength, id);
  };
  const markOverride = () => {
    setOverrideActive(true);
    setShowRateReset(false);
  };
  const changeCustomHours = (h: number) => {
    const hours = Math.min(10, Math.max(0.5, h));
    setCustomHours(hours);
    const hourly = artistsMap[artistId]?.rates.hourlyAud ?? 0;
    setDayRateInput(String(Math.round(hours * hourly)));
    setDepositInput(String(depositForDuration(hours * 60, artistsMap[artistId])));
    markOverride();
  };

  const toggleTag = (tag: string) =>
    setStyleTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const briefBookingFields = (): Partial<Booking> => ({
    placement: placement.placementNote.trim() || undefined,
    bodyPart: placement.bodyPart,
    bodyPartSide: placement.bodyPartSide,
    bodyPartQualifier: placement.bodyPartQualifier,
    colour,
    styleTags,
    dayRateAud:
      dayRateInput === '' ? standardDayRate : Math.max(0, Math.round(Number(dayRateInput) || 0)),
    dayRateBasis: dayRateBasis !== 'standard' ? dayRateBasis : undefined,
    summary: summary || undefined,
  });

  const applyEdits = (): Booking => {
    // Read the latest record so a just-sent deposit link isn't clobbered. Time
    // and piece assignment are intentionally not edited here (reschedule goes
    // through the calendar drag flow; piece is detach-only).
    const current = useBrainStore.getState().bookings[bookingId] ?? booking;
    const updated: Booking = {
      ...current,
      artistId,
      durationMin: effectiveDurationMin,
      sessionLength: effectiveSessionLength,
      notes,
      referencePhotos: selectedRefs,
      designBrief: deriveBrief(),
      invoiceStatus,
      ...briefBookingFields(),
    };
    upsertBooking(updated);
    return updated;
  };

  const rescheduleOnCalendar = () => {
    applyEdits();
    onClose();
    navigate(`/calendar/${booking.studioId}`, {
      state: { focusDayIso: booking.start, focusArtistId: booking.artistId, moveBookingId: booking.id },
    });
  };

  const detachPiece = () => {
    const brain = useBrainStore.getState();
    const bk = brain.bookings[bookingId];
    if (!bk?.pieceId) return;
    const p = brain.pieces[bk.pieceId];
    if (p) brain.upsertPiece({ ...p, bookingsLinked: p.bookingsLinked.filter((id) => id !== bookingId) });
    brain.upsertBooking({ ...bk, pieceId: undefined });
    setConfirmingDetach(false);
    pushNotification({
      intensity: 'info',
      title: 'Detached from piece',
      body: 'This booking is now standalone.',
      ttlMs: 3500,
      customerId: customer.id,
    });
  };

  const save = () => {
    if (movedOntoLeave) return;
    applyEdits();
    pushNotification({
      intensity: 'info',
      title: 'Booking updated',
      body: `${customerName(customer)} · ${artist?.displayName ?? artistId}`,
      ttlMs: 3500,
      customerId: customer.id,
    });
    onClose();
  };

  const sendDepositLink = () => {
    if (!canTakeDeposit) return;
    setDepositMenuOpen(false);
    applyEdits();
    const dep = generateDepositLink({
      customerId: booking.customerId,
      studioId: booking.studioId,
      artistId,
      amountAud: depositAud,
      channel: 'sms',
      sessionLength: effectiveSessionLength,
      source: 'agent-initiated-chat',
    });
    const brain = useBrainStore.getState();
    const created = brain.deposits[dep.id];
    if (created) brain.upsertDeposit({ ...created, bookingId: booking.id });
    const bk = brain.bookings[bookingId];
    if (bk) brain.upsertBooking({ ...bk, depositId: dep.id });
    // tv-k9b.5: no demo auto-pay. The link stays pending until the customer pays
    // it (the Customer Simulator or their pay page).
    pushNotification({
      intensity: 'high',
      title: 'Deposit link sent',
      body: `${aud(depositAud)} link sent to ${customerName(customer)}. It stays pending until they pay.`,
      ttlMs: 5000,
      customerId: customer.id,
    });
    onClose();
  };

  // In-person / phone deposit on THIS booking: ONE atomic intent (tv-2na.8.11) —
  // it pays the booking's pending link deposit, or creates + pays one, and the
  // booking ends firm with the deposit linked. No Stripe link, no reminder cadence.
  // The toast waits for the server, so it never says "paid" for a refused record.
  const takeManual = (method: 'cash' | 'eftpos' | 'bank-transfer') => {
    if (!canTakeDeposit || !manualContactEmail) return;
    setDepositMenuOpen(false);
    applyEdits();
    const methodLabel = method === 'bank-transfer' ? 'bank transfer' : method === 'eftpos' ? 'EFTPOS' : 'cash';
    void payBookingDepositAtDesk({
      bookingId,
      amountAud: depositAud,
      method,
      by: currentActorId() ?? 'front-desk',
      contactEmail: manualContactEmail,
      onConfirmed: ({ amountAud }) =>
        pushNotification({
          intensity: 'high',
          title: 'Deposit paid',
          body: `${aud(amountAud)} by ${methodLabel} · ${customerName(customer)}. Booking confirmed. No reminder cadence.`,
          ttlMs: 4000,
          customerId: customer.id,
        }),
      onFailed: (f) => {
        const copy = intentFailureCopy(f);
        pushNotification({ intensity: 'critical', title: copy.title, body: copy.body, ttlMs: 10000, customerId: customer.id });
      },
    });
  };

  const pieceTitle = pieceForBooking
    ? pieceForBooking.title || pieceForBooking.style || pieceForBooking.designType.replace(/-/g, ' ')
    : '';

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 8, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 8, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-label="Edit booking"
          data-testid="edit-booking-modal"
        >
          <button
            type="button"
            onClick={onClose}
            className="absolute top-3 right-3 text-cream-soft hover:text-cream"
            aria-label="Close"
          >
            <X size={16} />
          </button>

          <div className="flex items-center gap-2">
            <span className="label-small-caps text-gold-bright">Edit booking</span>
            <span
              className={clsx(
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest',
                statusTone.fill,
                statusTone.text,
              )}
              data-testid="edit-booking-status"
            >
              <span className={clsx('h-1.5 w-1.5 rounded-full', statusTone.dot)} />
              {statusTone.label}
            </span>
          </div>
          <h2 className="heading-display text-2xl text-cream mt-2">{customerName(customer)}</h2>

          {/* Artist */}
          <div className="block mt-5">
            <span className="label-small-caps">
              Artist <span className="text-blood">*</span>
            </span>
            <div className="mt-2">
              <ArtistPicker
                artists={studioArtists}
                value={artistId}
                excludeIds={onLeaveIds}
                onSelect={changePrimaryArtist}
                placeholder="Select artist"
                testid="edit-booking-artist"
                required
              />
            </div>
          </div>

          {/* Scheduled time — read-only; rescheduling goes through the calendar
              drag flow so every move is logged in the booking's history. */}
          <div className="mt-3">
            <span className="label-small-caps">When</span>
            <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-gold-dim/30 px-3 py-2">
              <span className="text-sm text-cream" data-testid="edit-booking-when">
                {simTimeFull(booking.start)}
              </span>
              <button
                type="button"
                onClick={rescheduleOnCalendar}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-gold-bright hover:text-gold-bright/80 transition"
                data-testid="edit-booking-reschedule"
              >
                <CalendarClock size={13} /> Reschedule on calendar
              </button>
            </div>
            <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint">
              Drag the tile to a new slot — the move is logged in the booking history.
            </p>
          </div>

          {/* Piece — display only; assignment can't be changed retroactively, only detached */}
          <div className="mt-3">
            <span className="label-small-caps">Piece</span>
            {pieceForBooking ? (
              <div className="mt-2 rounded-md border border-gold-dim/30 px-3 py-2" data-testid="edit-booking-piece">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-cream capitalize">
                    {pieceTitle} · session {sessionIndexInPiece(pieceForBooking, booking.id)}
                    {pieceForBooking.estimatedTotalSessions ? ` of ${pieceForBooking.estimatedTotalSessions}` : ''}
                  </span>
                  {!confirmingDetach ? (
                    <button
                      type="button"
                      onClick={() => setConfirmingDetach(true)}
                      className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-cream-faint hover:text-blood transition"
                      data-testid="edit-booking-piece-detach"
                    >
                      <Unlink size={12} /> Detach
                    </button>
                  ) : null}
                </div>
                {confirmingDetach ? (
                  <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-blood/40 bg-blood/5 px-2.5 py-1.5">
                    <span className="flex items-center gap-1 text-[11px] text-blood">
                      <AlertTriangle size={11} /> Detach from this piece? Progress continuity is lost.
                    </span>
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setConfirmingDetach(false)}
                        className="font-mono text-[10px] uppercase tracking-widest text-cream-faint hover:text-cream"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={detachPiece}
                        className="font-mono text-[10px] uppercase tracking-widest text-blood hover:text-cream"
                        data-testid="edit-booking-piece-detach-confirm"
                      >
                        Detach
                      </button>
                    </span>
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="mt-2 font-serif text-[13px] text-cream-soft">Standalone booking — not part of a piece.</p>
            )}
          </div>

          {/* References */}
          {references.photos.length > 0 ? (
            <div className="block mt-3">
              <ReferencePicker
                photos={references.photos}
                hiddenCount={references.hiddenCount}
                selected={selectedRefs}
                onChange={setSelectedRefs}
                label="References — tap what they sent to attach"
                testid="edit-booking-references"
              />
            </div>
          ) : null}

          {/* Placement */}
          <div className="mt-4">
            <PlacementFields
              value={placement}
              onChange={(patch) => setPlacement((p) => ({ ...p, ...patch }))}
              idPrefix="edit-booking"
            />
          </div>

          {/* Colour */}
          <div className="block mt-3">
            <span className="label-small-caps">Colour</span>
            <div className="mt-2 grid grid-cols-2 gap-2">
              {(['black-and-grey', 'colour'] as const).map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColour(c)}
                  className={
                    colour === c
                      ? 'rounded-md border border-gold-bright bg-gold/10 px-3 py-2 text-xs text-gold-bright'
                      : 'rounded-md border border-gold-dim/30 px-3 py-2 text-xs text-cream-soft hover:border-gold-bright/60'
                  }
                  data-testid={`edit-booking-colour-${c}`}
                >
                  {c === 'colour' ? 'Colour' : 'Black & grey'}
                </button>
              ))}
            </div>
          </div>

          {/* Style tags */}
          <div className="block mt-3">
            <span className="label-small-caps">Style tags</span>
            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="edit-booking-style-tags">
              {STYLE_TAG_OPTIONS.map((tag) => {
                const active = styleTags.includes(tag);
                return (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleTag(tag)}
                    className={clsx(
                      'rounded-full border px-2.5 py-1 text-[11px] capitalize transition',
                      active
                        ? 'border-gold-bright bg-gold/15 text-gold-bright'
                        : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                    )}
                  >
                    {tag.replace(/-/g, ' ')}
                  </button>
                );
              })}
            </div>
            {summary ? (
              <p className="mt-2 font-serif text-[12px] text-cream-soft" data-testid="edit-booking-summary">
                <span className="label-small-caps text-gold-bright mr-1.5">Summary</span>
                {summary}
              </p>
            ) : null}
          </div>

          {/* Session length + rate + deposit amount */}
          <div className="block mt-4">
            <span className="label-small-caps">Session &amp; rate</span>
            <div className="mt-2">
              <RateCardBlock
                artist={artist}
                sessionLength={sessionLength}
                onSessionChange={changeSession}
                dayRate={dayRateInput}
                onDayRateChange={(v) => {
                  setDayRateInput(v);
                  markOverride();
                }}
                customHours={customHours}
                onCustomHoursChange={changeCustomHours}
                dayRateBasis={dayRateBasis}
                onDayRateBasisChange={(v) => {
                  setDayRateBasis(v);
                  markOverride();
                }}
                deposit={depositInput}
                onDepositChange={(v) => {
                  setDepositInput(v);
                  markOverride();
                }}
                standardDayRate={standardDayRate}
                standardDeposit={standardDeposit}
                usingPiecePlan={usingPiecePlan}
                showRateReset={showRateReset}
                onResetToStandard={() => syncStandard(sessionLength, artistId)}
                idPrefix="edit-booking"
              />
            </div>
          </div>

          {/* Notes */}
          <label className="block mt-3">
            <span className="label-small-caps">Notes</span>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Session-specific detail only (allergy specifics, cover-up context, temporary marks, tricky positioning). Do not enter deposit, rebook, day rate, or design info — those have fields above."
              className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
            />
          </label>

          {/* Invoice status */}
          <label className="block mt-3">
            <span className="label-small-caps">Invoice</span>
            <select
              value={invoiceStatus}
              onChange={(e) => setInvoiceStatus(e.target.value as InvoiceStatus)}
              className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
              data-testid="edit-booking-invoice"
            >
              {INVOICE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          {/* Deposit */}
          <div className="mt-4 rounded-md border border-gold-dim/30 p-3">
            <div className="flex items-center justify-between">
              <span className="label-small-caps text-cream-soft">Deposit</span>
              <span
                className={clsx('text-xs', shade.level === 'full' ? 'text-mint' : 'text-gold-bright')}
                data-testid="edit-booking-deposit-state"
              >
                {linkedDeposit
                  ? `${shade.label} · ${aud(linkedDeposit.amountPaid ?? (linkedDeposit.status === 'paid' ? linkedDeposit.amountAud : 0))} of ${aud(linkedDeposit.amountAud)}`
                  : 'None on file'}
              </span>
            </div>
            <p className="mt-2 font-serif text-[12px] text-cream-soft">
              Amount is set in <span className="text-cream">Session &amp; rate</span> above ({aud(depositAud)}). Send a
              Stripe link or record an in-person payment. Trusted clients can pay in full on the day.
            </p>
            <div className="mt-2 flex justify-end">
              <div className="relative" ref={depositMenuRef}>
                <button
                  type="button"
                  onClick={() => setDepositMenuOpen((v) => !v)}
                  disabled={!canTakeDeposit}
                  className="btn-ghost text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="edit-booking-take-deposit"
                  aria-haspopup="menu"
                  aria-expanded={depositMenuOpen}
                >
                  Take deposit {aud(depositAud)} <ChevronDown size={12} />
                </button>
                {depositMenuOpen ? (
                  <div
                    className="absolute bottom-full right-0 mb-1 w-60 panel-dark py-1 shadow-ink-soft"
                    data-testid="edit-booking-take-deposit-menu"
                    role="menu"
                  >
                    <button
                      type="button"
                      onClick={sendDepositLink}
                      className="w-full text-left px-3 py-2 text-sm text-cream hover:bg-ink-soft/60 transition flex items-center gap-2"
                      data-testid="edit-booking-send-deposit"
                    >
                      <Send size={13} className="text-gold-bright shrink-0" /> Send Ink Book / Stripe link
                    </button>
                    <div className="my-1 border-t border-gold-dim/20" />
                    <DepositEmailField customer={customer} value={manualEmail} onChange={setManualEmail} className="px-3 py-1.5" />
                    {(
                      [
                        { m: 'cash', label: 'Paid by cash' },
                        { m: 'eftpos', label: 'Paid by EFTPOS' },
                        { m: 'bank-transfer', label: 'Paid by bank transfer' },
                      ] as const
                    ).map((opt) => (
                      <button
                        key={opt.m}
                        type="button"
                        onClick={() => takeManual(opt.m)}
                        disabled={!manualContactEmail}
                        title={manualContactEmail ? undefined : "Add the client's email to take the deposit now"}
                        className="w-full text-left px-3 py-2 text-sm text-cream hover:bg-ink-soft/60 transition flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                        data-testid={`edit-booking-take-deposit-${opt.m}`}
                      >
                        <Banknote size={13} className="text-mint shrink-0" /> {opt.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="flex gap-2 mt-5 justify-end">
            <button type="button" onClick={onClose} className="btn-ghost text-xs">
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={movedOntoLeave}
              className="btn-primary text-xs disabled:opacity-40"
              data-testid="edit-booking-save"
            >
              <Save size={12} /> Save changes
            </button>
          </div>
          {movedOntoLeave ? (
            <p className="mt-2 text-right text-sm text-blood" role="alert" data-testid="edit-booking-artist-on-leave">
              {artist?.displayName ?? 'That artist'} is on leave that day. Pick another artist.
            </p>
          ) : null}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
